import {
  Box,
  Flex,
  Heading,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tooltip,
  Td,
  Tr,
  useToast,
} from '@chakra-ui/react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  GetDeliverablesDeliverableDataFragmentGQLType,
  useGetDeliverablesQuery,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DeliverableDependenciesPopover } from './DeliverableDependenciesPopover';
import {
  ColumnFilter,
  customMultiFilter,
  customFullDateStrFilter,
} from './DeliverablesColumnFilter';
import { StatusText } from './StatusContainer';
import { ComputedStatus, getDeliverablesUrl, getComputedStatus } from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons/fontAwesome';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { logOutOnAuthErrors } from 'packages/common-frontend/utils';

type DeliverablesTableDeliverable = GetDeliverablesDeliverableDataFragmentGQLType & {
  computedStatus: string;
  computedCurrentSubmission: Util.FullDateStr | '-';
};

const getCurrentSubmission = (
  deliverable: GetDeliverablesDeliverableDataFragmentGQLType,
): Util.FullDateStr | '-' =>
  deliverable.currentSubmission?.__typename === 'DeliverableFileSubmission'
    ? commonDate.epochMsToFullDateStr(deliverable.currentSubmission.created_at)
    : '-';

const getRowBGColor = (
  deliverable: GetDeliverablesDeliverableDataFragmentGQLType,
) => {
  const { status, dueDate } = deliverable;
  const computedStatus = getComputedStatus(status, dueDate);

  switch (computedStatus) {
    // case 'pendingApproval':
    //   return 'orange.300';'
    case 'overdue':
      return 'red.100';
    default:
      return 'none';
  }
};

// allows for empty dates which commonDate does not
const fullDateStrToHumanDateString = (dateStr: string) => {
  if (commonTypeguards.isFullDateStr(dateStr))
    return commonDate.fullDateStrToHumanDateString(dateStr);

  return dateStr;
};

const defaultSort = [
  { id: 'computedStatus', desc: true },
  { id: 'dueDate', desc: true },
];
const computedStatusSortOrder: { [key in ComputedStatus]: number } = {
  overdue: 5,
  upcoming: 4,
  submitted: 3,
  resubmit: 2,
  waived: 1,
};

const statusOptions = [
  { label: <StatusText computedStatus="submitted" />, value: 'submitted' },
  { label: <StatusText computedStatus="upcoming" />, value: 'upcoming' },
  { label: <StatusText computedStatus="overdue" />, value: 'overdue' },
  { label: <StatusText computedStatus="waived" />, value: 'waived' },
];

type DeliverablesTableProps = {
  deliverables: DeliverablesTableDeliverable[];
};
const DeliverablesTable = ({ deliverables }: DeliverablesTableProps) => {
  const [sorting, setSorting] = React.useState<SortingState>(defaultSort);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const location = useLocation();
  const navigate = useNavigate();

  const deliverablesUrl = getDeliverablesUrl(location.pathname);
  const columns: ColumnDef<DeliverablesTableDeliverable, any>[] = [
    {
      accessorKey: 'creditFacilityName',
      header: 'Credit Facility',
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Credit Facility/Facilities',
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: 'reportType',
      accessorKey: 'deliverableType',
      header: () => 'Report Type',
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Report Type(s)',
      },
    },
    {
      id: 'computedCurrentSubmission',
      accessorKey: 'computedCurrentSubmission',
      header: () => 'Submitted Date',
      cell: ({ getValue }) => {
        const dateStr = getValue();
        return fullDateStrToHumanDateString(dateStr);
      },
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Submitted Date',
        filterType: 'date',
      },
    },
    {
      id: 'dueDate',
      accessorKey: 'dueDate',
      header: () => 'Due Date',
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Due Date',
        filterType: 'date',
      },
    },
    {
      id: 'computedStatus',
      accessorKey: 'computedStatus',
      header: () => 'Status',
      /* eslint-disable react/display-name */
      cell: ({ row }) => (
        <StatusText
          computedStatus={getComputedStatus(
            row.original.status,
            row.original.dueDate,
          )}
        />
      ),
      sortingFn: (rowA, rowB) => {
        const statusA = getComputedStatus(
          rowA.original.status,
          rowA.original.dueDate,
        );
        const statusB = getComputedStatus(
          rowB.original.status,
          rowB.original.dueDate,
        );
        // eslint-disable-next-line no-nested-ternary
        return computedStatusSortOrder[statusA] >
          computedStatusSortOrder[statusB]
          ? 1
          : computedStatusSortOrder[statusA] < computedStatusSortOrder[statusB]
          ? -1
          : 0;
      },
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Status(es)',
        filterOptions: statusOptions,
      },
    },
    {
      id: 'dependenciesAndClear',
      accessorKey: 'dependencies',
      header: ({ table }) => (
        <Text
          color={colors.blue[400]}
          cursor="pointer"
          onClick={() => {
            table.resetColumnFilters();
            table.resetSorting();
            setColumnFilters([]);
            setSorting(defaultSort);
          }}
        >
          Clear
        </Text>
      ),

      cell: ({ row }) => {
        const { dependencies } = row.original;
        return (
          <>
            {!!dependencies.length && (
              <DeliverableDependenciesPopover deliverable={row.original} />
            )}
          </>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        style: { textAlign: 'center' },
      },
    },
    {
      id: 'moreDetail',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const {
          isDeliverableViewPermissionedParty,
          isDeliverableResponsibleParty,
          id,
        } = row.original;
        const canView = Boolean(
          isDeliverableResponsibleParty || isDeliverableViewPermissionedParty,
        );

        return (
          <>
            {canView && (
              <Tooltip bg="gray.500" hasArrow label="More details">
                <IconButton
                  aria-label="more details"
                  icon={<FaIcon icon="chevron-right" />}
                  variant="ghost"
                  onClick={() =>
                    navigate(
                      deliverablesUrl
                        ? `${deliverablesUrl}/${id}`
                        : `/deliverables/${id}`,
                    )
                  }
                />
              </Tooltip>
            )}
          </>
        );
      },
      meta: {
        style: {
          padding: '0px',
          textAlign: 'right',
        },
      },
    },
  ];

  const table = useReactTable({
    data: deliverables,
    columns,
    enableMultiSort: true,
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize: 35,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <Box>
      <Table borderWidth="1px" borderColor={colors.gray[100]}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                const columnCanFilter = header.column.getCanFilter();

                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={colors.black[95]}
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                        color={colors.aurora[400]}
                        fontWeight="thin"
                      >
                        {{
                          asc: <FaIcon icon="sort-up" />,
                          desc: <FaIcon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <FaIcon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {columnCanFilter && (
                        <ColumnFilter
                          type={header.column.columnDef.meta?.filterType}
                          header={header.column.columnDef.meta?.filterHeader}
                          options={header.column.columnDef.meta?.filterOptions}
                          column={header.column}
                          valueFormatFn={
                            header.column.columnDef.meta?.filterFormatFn
                          }
                        />
                      )}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} backgroundColor={getRowBGColor(row.original)}>
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  textAlign={cell.column.columnDef.meta?.style?.textAlign}
                  padding={cell.column.columnDef.meta?.style?.padding}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <TanStackPaginationControl table={table} />
    </Box>
  );
};

type DeliverablesTanStackProps = {
  responsiblePartyId: string;
  activeCreditFacilities: { slug: string; id: string }[];
  organizationSelect?: React.ReactElement;
  optionalDescription?: React.ReactElement;
  queryParams?: {
    creditFacilityIds?: string[];
  };
};
export const DeliverablesTanStack = ({
  responsiblePartyId,
  organizationSelect,
  activeCreditFacilities,
  optionalDescription,
  queryParams,
}: DeliverablesTanStackProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [creditFacilityIds] = useState<string[]>(
    queryParams?.creditFacilityIds || _.map(activeCreditFacilities, 'id'),
  );
  const [organizationId] = useState<string | null>(responsiblePartyId);

  const { data, loading } = useGetDeliverablesQuery({
    fetchPolicy: 'no-cache',
    variables: {
      responsiblePartyId,
      organizationId,
      creditFacilityIds,
      limit: 100,
    },
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, navigate);
    },
  });

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (
    !data ||
    !data.deliverables ||
    loading ||
    !userData ||
    !userData.user ||
    userLoading
  ) {
    return <Skeleton data-testid="loading-deliverables-view" height="600px" />;
  }

  const {
    deliverables: { nodes: deliverablesData },
  } = data;

  const deliverables = deliverablesData.map<DeliverablesTableDeliverable>(
    (d) => ({
      ...d,
      computedStatus: getComputedStatus(d.status, d.dueDate),
      computedCurrentSubmission: getCurrentSubmission(d),
    }),
  );

  return (
    <Box height="100%">
      <Flex justify="space-between">
        <Heading as="h2" mb={optionalDescription ? '8px' : '16px'} pb="16px">
          Deliverables
        </Heading>
        <Box>{organizationSelect}</Box>
      </Flex>
      {optionalDescription && <Box mb="32px">{optionalDescription}</Box>}
      <DeliverablesTable deliverables={deliverables} />
    </Box>
  );
};
